/* eslint-disable no-unused-vars */
import React, { useMemo, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  withStyles,
  Typography,
  makeStyles
} from '@material-ui/core';
import { formatCurrency } from 'src/utils';
import { colors } from 'src/constants';
import { SupplierWeeklyData } from '..';
import html2canvas from 'html2canvas';
import PropTypes from 'prop-types';

const columnColors = [
  colors.orange[100], // light orange
  colors.blue[100], // light blue
  colors.green[100], // light green
  colors.pink[100], // light pink
  colors.purple[100], // light purple
  colors.lime[100], // lime green
  colors.yellow[100] // light yellow
];

// Custom styles (MUI v4 doesn't support sx)
const StyledTableCell = withStyles(() => ({
  head: {
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    backgroundColor: '#f5f5f5'
  },
  body: {
    whiteSpace: 'nowrap'
  }
}))(TableCell);

const StyledTableRow = withStyles(() => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fafafa'
    }
  }
}))(TableRow);

const useStyles = makeStyles(() => ({
  clickableCell: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.08)' // light gray highlight
    }
  },
  weekCell: {
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      opacity: 0.6
    },
    fontSize: '17px',
    fontWeight: 500
  },
  headerCell: {
    fontSize: '16px',
    fontWeight: 'bold'
  },
  supplierCell: {
    fontSize: '15px',
    fontWeight: 'bold',
    minWidth: '200px'
  }
}));

interface Props {
  hideZeroVal?: boolean;
  liabilityChart: SupplierWeeklyData[]; // later define proper type
  onHandleOpenLiabilityCheque: (week?: string, supplier_id?: number) => void;
}

interface Ref {
  capture: () => void;
}

const LiabilityChartTable = forwardRef<Ref, Props>(
  (
    { hideZeroVal = false, liabilityChart, onHandleOpenLiabilityCheque },
    ref
  ) => {
    const classes = useStyles();

    // Dynamically gather all unique week keys
    const allWeeks = useMemo(() => {
      if (!liabilityChart) return [];

      // get all unique weeks first
      const weeks = Array.from(
        new Set(liabilityChart.flatMap((item) => Object.keys(item.weeks)))
      );

      // filter out weeks where all values are "0" or "0.00"
      const filteredWeeks = weeks.filter((week) =>
        liabilityChart.some((item) => parseFloat(item.weeks[week] || '0') !== 0)
      );
      return hideZeroVal ? filteredWeeks : weeks;
    }, [hideZeroVal, liabilityChart]);

    const columnTotals = useMemo(() => {
      const totals: any = {};

      allWeeks.forEach((week: any) => {
        totals[week] = liabilityChart.reduce((sum: any, item: any) => {
          const value = item.weeks?.[week] ?? 0;
          return sum + value;
        }, 0);
      });

      totals.total = liabilityChart.reduce(
        (sum: number, item: any) =>
          sum + parseFloat((item.total_amount || '0').replace(/,/g, '')),
        0
      );

      return totals;
    }, [liabilityChart, allWeeks]);

    const tableRef = useRef<HTMLTableElement>(null);

    const handleCapture = async () => {
      //capture table as PNG
      if (tableRef.current) {
        const canvas = await html2canvas(tableRef.current, {
          scale: 2,
          backgroundColor: '#fff'
        });
        const dataURL = canvas.toDataURL('image/png');

        const link = document.createElement('a');
        link.href = dataURL;
        link.download = 'liability-table.png';
        link.click();
      }
    };

    useImperativeHandle(ref, () => ({
      capture: handleCapture
    }));

    return (
      <TableContainer
        component={Paper}
        style={{ marginTop: 20, marginBottom: 20 }}
      >
        <Table ref={tableRef}>
          <TableBody>
            <StyledTableRow style={{ backgroundColor: colors.blueGrey[200] }}>
              <StyledTableCell>
                <Typography variant="h4">TOTAL</Typography>
              </StyledTableCell>
              {allWeeks?.map((week: any, i) => (
                <TableCell
                  key={i}
                  className={classes.clickableCell}
                  align="right"
                  onClick={() => onHandleOpenLiabilityCheque(week)}
                >
                  <Typography variant="h4">
                    {formatCurrency(columnTotals[week] || 0)}
                  </Typography>
                </TableCell>
              ))}
              <TableCell align="right">
                <Typography variant="h4" color="primary">
                  {formatCurrency(columnTotals.total)}
                </Typography>
              </TableCell>
            </StyledTableRow>
          </TableBody>

          <TableHead>
            <TableRow>
              <StyledTableCell className={classes.headerCell}>
                Supplier
              </StyledTableCell>
              {allWeeks?.map((week, idx) => (
                <StyledTableCell
                  key={idx}
                  align="right"
                  className={classes.headerCell}
                >
                  {week}
                </StyledTableCell>
              ))}
              <StyledTableCell align="right">
                <Typography variant="h5">Total</Typography>
              </StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {liabilityChart?.map((row: any, idx: any) => (
              <StyledTableRow key={idx}>
                <TableCell className={classes.supplierCell}>
                  {row.supplier}
                </TableCell>
                {allWeeks?.map((week, i) => (
                  <TableCell
                    key={i}
                    align="right"
                    style={{
                      backgroundColor: columnColors[i % columnColors.length]
                    }}
                    className={classes.weekCell}
                    onClick={() => {
                      onHandleOpenLiabilityCheque(
                        week,
                        row?.supplier_id || null
                      );
                    }}
                  >
                    {/* TODO: Fix later */}
                    {/* @ts-ignore:next-line */}
                    {row.weeks[week] != null
                      ? // @ts-ignore:next-line
                        formatCurrency(row?.weeks?.[week] || 0)
                      : '--'}
                    {/* @ts-ignore:next-line */}
                  </TableCell>
                ))}
                <TableCell align="right">
                  <Typography variant="h5" color="primary">
                    {formatCurrency(
                      parseFloat((row.total_amount || '0').replace(/,/g, '')) ||
                        0
                    )}
                  </Typography>
                </TableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  }
);
LiabilityChartTable.displayName = 'LiabilityChartTable';

LiabilityChartTable.propTypes = {
  hideZeroVal: PropTypes.bool,
  liabilityChart: PropTypes.array.isRequired,
  onHandleOpenLiabilityCheque: PropTypes.func.isRequired
};

export default LiabilityChartTable;
